import {
  PLACEHOLDER_ATTRIBUTE,
  PLACEHOLDER_CLASS,
  PLACEHOLDER_FILLED_CLASS,
} from './nodes/PlaceholderNode';

export interface TextTemplate {
  id: string;
  name: string;
  /** Plain text, one line per paragraph; `$Name$` marks a tab stop. */
  content: string;
}

export type TemplateSegment =
  | { type: 'text'; text: string }
  | { type: 'placeholder'; name: string };

const PLACEHOLDER_PATTERN = /\$([^$\n]+)\$/g;

/** Splits a template into lines, each a run of text and placeholder segments. */
export function parseTemplate(content: string): TemplateSegment[][] {
  return content
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => {
      const segments: TemplateSegment[] = [];
      let lastIndex = 0;
      for (const match of line.matchAll(PLACEHOLDER_PATTERN)) {
        if (match.index > lastIndex) {
          segments.push({
            type: 'text',
            text: line.slice(lastIndex, match.index),
          });
        }
        segments.push({ type: 'placeholder', name: match[1].trim() });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < line.length) {
        segments.push({ type: 'text', text: line.slice(lastIndex) });
      }
      return segments;
    });
}

function parseHtml(html: string) {
  return new DOMParser().parseFromString(html, 'text/html');
}

export function countUnfilledPlaceholders(html: string): number {
  if (!html.includes(PLACEHOLDER_ATTRIBUTE)) {
    return 0;
  }
  return parseHtml(html).querySelectorAll(`[${PLACEHOLDER_ATTRIBUTE}]`).length;
}

export function resolvePlaceholderMarkup(html: string): string {
  if (!html.includes(PLACEHOLDER_CLASS)) {
    return html;
  }
  const dom = parseHtml(html);
  dom.querySelectorAll(`[${PLACEHOLDER_ATTRIBUTE}]`).forEach((element) => {
    element.textContent = `[${element.getAttribute(PLACEHOLDER_ATTRIBUTE)}]`;
    element.removeAttribute(PLACEHOLDER_ATTRIBUTE);
  });
  dom.querySelectorAll(`.${PLACEHOLDER_CLASS}`).forEach((element) => {
    element.classList.remove(PLACEHOLDER_CLASS, PLACEHOLDER_FILLED_CLASS);
    if (element.classList.length === 0) {
      element.removeAttribute('class');
    }
  });
  return dom.body.innerHTML;
}
